import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import React from 'react';
import _ from 'underscore';
import { Formatters, Messages } from '../../../../codegen/Localize';

import { CommonStyles } from '../../../../common/react/CommonStyles';
import { cssClass, textEllipsis } from '../../../../style';
import { INamedItemInfo } from '../../../../ts/models/resourceInfoParts';
import { PlainTextCellWithCount } from './PlainTextAndNumberCells';

export type PopoverTitleMessageFn = (a: {
  COUNT: number;
  COUNT_LOC: string;
}) => string;

export function ItemsCellWithPopover(
  getItemsForRow: (row: DataGrid.Row) => ReadonlyArray<INamedItemInfo>,
  popoverTitleMessage: PopoverTitleMessageFn,
): React.FunctionComponent<BodyCell.Props> {
  return function ItemsCellWithPopover(props: BodyCell.Props): JSX.Element {
    const items = getItemsForRow(props.row);
    const itemCount = items.length;
    const firstItemName = itemCount > 0 ? items.find(item => item !== undefined).name : '';
    return PlainTextCellWithCount(
      firstItemName,
      getListPopoverContent(getItemsForRow, popoverTitleMessage),
      additionalItemsText(itemCount)
    )(props);
  };
}

const cssPrefix = 'ItemsCellPopover';

const popoverRows = cssClass(cssPrefix, 'rows', {
  marginTop: CommonStyles.Sizes.DefaultSpace
});

const popoverRow = cssClass(cssPrefix, 'row', textEllipsis, {
  height: CommonStyles.Sizes.CompactRowHeight,
  lineHeight: CommonStyles.Sizes.CompactRowHeight + 'px',
  cursor: 'default'
});

function getListPopoverContent(
  getItemsForRow: (row: DataGrid.Row) => ReadonlyArray<INamedItemInfo>,
  popoverTitleMessage: PopoverTitleMessageFn,
): (props: BodyCell.Props) => Promise<JSX.Element> {
  return (props: BodyCell.Props) => {
    const items = getItemsForRow(props.row);
    const titleString = popoverTitleMessage({
      COUNT: items.length,
      COUNT_LOC: Formatters.formatCount(items.length),
    });
    return Promise.resolve(
      <div data-tb-test-id="popover-menu-panel">
        <div className="tb-content-item-popover-title">{titleString}</div>
        <div className={popoverRows}>
          {_.map(items, listPopoverRow)}
        </div>
      </div>
    );
  };
}

function listPopoverRow(item: INamedItemInfo): JSX.Element {
  return (
    <div className={popoverRow} key={item.id}>
      <span title={item.name}>{item.name}</span>
    </div>
  );
}

function additionalItemsText(itemCount: number): string {
  if (itemCount === 0 || itemCount === 1) {
    return '';
  }

  return Messages.additionalItemsPillText({
    ADDITIONAL_COUNT_TEXT: Formatters.formatCount(itemCount - 1)
  });
}
